/* One service or asset: its fields, its scores, what it depends on, what
   depends on it, and the work attached to it.

   Edits save on blur rather than behind a Save button. With a form this small
   a Save button mostly produces abandoned edits -- someone types a number,
   navigates away, and the change is gone with no indication it was lost. */
import { useCallback, useEffect, useState } from "react";

import { api, plural, type Kind, type TrackedObject } from "../api";
import {
  Chip,
  CollapsiblePanel,
  DimensionIcon,
  DueDate,
  Field,
  Panel,
  ScaleDialog,
  Score,
  hintFor,
  priorityTone,
  scoreStyle,
} from "../components/ui";
import { BetChips, InitiativePicker } from "../components/strategy";
import { usePermissions } from "../auth";
import { go, href } from "../route";
import { useStore } from "../store";

export default function ObjectDetail({ kind, id }: { kind: Kind; id: number }) {
  const { meta, objects, people, initiatives, run, refresh } = useStore();
  const [object, setObject] = useState<TrackedObject | null>(null);
  const [missing, setMissing] = useState(false);
  const [cycle, setCycle] = useState<string | null>(null);
  const [scale, setScale] = useState<string | null>(null);
  const { canDelete, canWrite } = usePermissions();

  const load = useCallback(async () => {
    try {
      setObject(await api.get(plural(kind), id));
      setMissing(false);
    } catch {
      setMissing(true);
    }
  }, [kind, id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (missing) return <p className="note">No such {kind}. It may have been deleted.</p>;
  if (!object || !meta) return <p className="note">Loading…</p>;

  const dimensions = object.kind === "service" ? meta.service_dimensions : meta.asset_dimensions;
  const classes = object.kind === "service" ? meta.service_classes : meta.asset_classes;

  const patch = async (body: Record<string, unknown>) => {
    const updated = await run(() => api.patch(plural(object.kind), object.id, body));
    if (updated) await load();
  };

  const addDependency = async (toKind: Kind, toId: number, depKind: string) => {
    const result = await run(() =>
      api.addDependency(plural(object.kind), object.id, toKind, toId, depKind),
    );
    if (result) {
      setCycle(
        result.cycle
          ? "That edge closes a dependency cycle. It has been recorded anyway — this tool " +
              "describes the portfolio rather than arguing with it — but it is worth checking."
          : null,
      );
      await load();
    }
  };

  /* Assets cannot depend on services: the model has no table for it, and
     nothing in the portfolio has needed one. So the picker for an asset offers
     only assets. */
  const candidates = objects.filter(
    (o) =>
      !(o.kind === object.kind && o.id === object.id) &&
      (object.kind === "service" || o.kind === "asset"),
  );

  return (
    <>
      <p className="crumbs">
        <a className="crumb" href={href.list(object.kind)}>
          {object.kind === "service" ? "Services" : "Assets"}
        </a>{" "}
        / {object.name}
      </p>
      <div className="panel-hd" style={{ marginBottom: 14 }}>
        <h1 style={{ fontFamily: "var(--display)", fontSize: 22, margin: 0 }}>{object.name}</h1>
        <span className="row tight">
          <Chip text={object.class} />
          <Chip text={object.priority} tone={priorityTone(object.priority)} />
          <span className="num" style={{ fontSize: 20, fontWeight: 700 }}>
            <Score value={object.maturity.score} />
          </span>
          <span className="muted" style={{ fontSize: 11 }}>
            {object.maturity.assessed}/{object.maturity.of} assessed
          </span>
        </span>
      </div>

      {cycle && <div className="warnbox">{cycle}</div>}

      <ScaleDialog
        dimension={scale}
        label={dimensions.find((d) => d.key === scale)?.label ?? ""}
        onClose={() => setScale(null)}
      />

      <div className="detail">
        <div>
          <Panel title="Details">
            <Field label="Name">
              <input
                type="text"
                defaultValue={object.name}
                key={`name-${object.updated_at}`}
                onBlur={(e) => e.target.value !== object.name && patch({ name: e.target.value })}
              />
            </Field>
            <Field label="Description">
              <textarea
                rows={10}
                defaultValue={object.description}
                key={`desc-${object.updated_at}`}
                onBlur={(e) =>
                  e.target.value !== object.description && patch({ description: e.target.value })
                }
              />
            </Field>
            <div className="row">
              <Field label="Class">
                <select value={object.class} onChange={(e) => patch({ class: e.target.value })}>
                  {classes.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </Field>
              <Field label="Lifecycle stage">
                <select
                  value={object.lifecycle_stage}
                  onChange={(e) => patch({ lifecycle_stage: e.target.value })}
                >
                  {meta.lifecycle_stages.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </Field>
              <Field label="Priority">
                <select
                  value={object.priority}
                  onChange={(e) => patch({ priority: e.target.value })}
                >
                  {meta.priorities.map((p) => (
                    <option key={p}>{p}</option>
                  ))}
                </select>
              </Field>
              <Field label="Owner">
                <select
                  value={object.owner_id ?? ""}
                  onChange={(e) =>
                    patch({ owner_id: e.target.value === "" ? null : Number(e.target.value) })
                  }
                >
                  <option value="">Unassigned</option>
                  {people.map((p) => (
                    <option key={p.person_id} value={p.person_id}>
                      {p.display_name}
                    </option>
                  ))}
                </select>
              </Field>
              {/* Why this work exists. The bets are read-only here: an
                  initiative serves them, this object serves the initiative,
                  and editing that relationship two levels down would make the
                  same link editable in two places. */}
              <Field label="Initiative">
                <InitiativePicker
                  value={object.initiative_id}
                  disabled={!canWrite}
                  currentLabel={object.name}
                  onChange={(next) => patch({ initiative_id: next })}
                />
              </Field>
            </div>
            {object.initiative && (
              <p className="meta-line">
                Serves <a href={href.initiative(object.initiative.id)}>
                  {object.initiative.code} — {object.initiative.name}
                </a>
                <BetChips
                  betIds={
                    initiatives.find((i) => i.id === object.initiative_id)?.bet_ids ?? []
                  }
                />
              </p>
            )}
            <Field label="Contributors">
              <div className="row tight">
                {object.contributors.map((c) => (
                  <span key={c.person_id} className="chip">
                    {c.display_name}
                    <button
                      className="linkbtn"
                      style={{ marginLeft: 6 }}
                      onClick={() =>
                        patch({
                          contributors: object.contributors
                            .filter((x) => x.person_id !== c.person_id)
                            .map((x) => x.person_id),
                        })
                      }
                    >
                      ×
                    </button>
                  </span>
                ))}
                <select
                  value=""
                  onChange={(e) =>
                    e.target.value &&
                    patch({
                      contributors: [
                        ...object.contributors.map((x) => x.person_id),
                        Number(e.target.value),
                      ],
                    })
                  }
                >
                  <option value="">add…</option>
                  {people
                    .filter(
                      (p) =>
                        p.person_id !== object.owner_id &&
                        !object.contributors.some((c) => c.person_id === p.person_id),
                    )
                    .map((p) => (
                      <option key={p.person_id} value={p.person_id}>
                        {p.display_name}
                      </option>
                    ))}
                </select>
              </div>
            </Field>
          </Panel>

          {object.concept && (
            <CollapsiblePanel
              title="Strategic concept"
              aside={
                <span className="muted">
                  {Object.values(object.concept).filter((v) => v.trim()).length} of 7 answered
                </span>
              }
            >
              <div className="concept">
                {(meta.strategy_elements ?? []).map((element) => (
                  <Field label={element.label} key={element.key}>
                    <textarea
                      rows={8}
                      placeholder={element.prompt}
                      /* Keyed on the object, not on updated_at. Saving one
                         element bumps updated_at, and keying on that would
                         remount all seven -- discarding whatever had been
                         typed into a neighbouring box while the first save
                         was still in flight. Seven adjacent fields make that
                         a matter of when, not whether. */
                      key={`${element.key}-${object.id}`}
                      defaultValue={object.concept?.[element.key] ?? ""}
                      onBlur={(e) => {
                        const was = object.concept?.[element.key] ?? "";
                        if (e.target.value.trim() === was.trim()) return;
                        void patch({ concept: { [element.key]: e.target.value } });
                      }}
                    />
                  </Field>
                ))}
              </div>
            </CollapsiblePanel>
          )}

          {object.links !== null && object.links !== undefined && (
            <Panel
              title="Documents"
              aside={<span className="muted">{object.links.length}</span>}
            >
              <ul className="deps">
                {object.links.map((link) => (
                  <li key={link.link_id}>
                    <a href={link.url} target="_blank" rel="noreferrer">
                      {link.title || link.url}
                    </a>
                    <button
                      className="linkbtn"
                      style={{ marginLeft: "auto" }}
                      onClick={async () => {
                        await run(() =>
                          api.removeObjectLink(plural(object.kind), object.id, link.link_id),
                        );
                        await load();
                      }}
                    >
                      remove
                    </button>
                  </li>
                ))}
                {object.links.length === 0 && (
                  <li className="muted">
                    no documents yet — positioning, pricing, the one-pager, the deck
                  </li>
                )}
              </ul>
              <NewLink objectKind={object.kind} objectId={object.id} onDone={load} />
            </Panel>
          )}

          <Panel title="Tasks" aside={<span className="muted">{object.tasks?.length ?? 0}</span>}>
            {(object.tasks ?? []).map((t) => (
              <article className="tcard" key={t.task_id}>
                <h4>
                  <a href={href.task(t.task_id)}>{t.title}</a>
                </h4>
                <div className="meta">
                  <span>{t.status}</span>
                  <DueDate date={t.next_milestone_date} status={t.status} />
                  <span>{t.owner_name ?? "Unassigned"}</span>
                  {t.subtasks > 0 && (
                    <span>
                      checklist {t.subtasks_done}/{t.subtasks}
                    </span>
                  )}
                </div>
              </article>
            ))}
            <NewTask objectKind={object.kind} objectId={object.id} onDone={load} />
          </Panel>
        </div>

        <div>
          <Panel title="Maturity">
            <div className="scores">
              {dimensions.map((d) => (
                <div className="s" key={d.key}>
                  {/* The label is the way in to the scale. This is the screen
                      where the number is decided, so the six anchor tests need
                      to be one click away from the input. */}
                  <button
                    className="dimbtn scorelabel"
                    title={`${hintFor(d.key, d.label, object.kind)} — click for the scale`}
                    onClick={() => setScale(d.key)}
                  >
                    <DimensionIcon dimension={d.key} />
                    <span>{d.label}</span>
                  </button>
                  <input
                    id={`score-${d.key}`}
                    type="number"
                    min={0}
                    max={100}
                    placeholder="—"
                    key={`${d.key}-${object.updated_at}`}
                    defaultValue={object.scores[d.key] ?? ""}
                    onBlur={(e) => {
                      const next = e.target.value.trim() === "" ? null : Number(e.target.value);
                      if (next !== object.scores[d.key]) void patch({ [d.key]: next });
                    }}
                  />
                  <div className="track">
                    <i
                      style={{
                        width: `${object.scores[d.key] ?? 0}%`,
                        ...scoreStyle(object.scores[d.key]),
                        opacity: 1,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <p className="note" style={{ marginTop: 12 }}>
              Empty means not assessed and counts as zero. The score above is the plain mean
              of all {object.maturity.of} dimensions, {object.maturity.assessed} of which carry
              a value.
            </p>
          </Panel>

          {/* Deleting an object is an admin act -- it destroys authored work,
              and the task log with it -- so a contributor is not shown the
              control. The server refuses it either way. */}
          {canDelete && (
          <Panel title="Danger">
            <button
              className="btn"
              onClick={async () => {
                if (!confirm(`Delete "${object.name}"? This cannot be undone.`)) return;
                await run(() => api.remove(plural(object.kind), object.id));
                await refresh();
                go(object.kind === "service" ? "/" : "/assets");
              }}
            >
              Delete this {object.kind}
            </button>
            <p className="note">
              Tasks attached to it are unattached, not deleted. Dependencies referring to it are
              removed.
            </p>
          </Panel>
          )}
        </div>
      </div>

      {/* Last on the page and closed by default. Dependencies are consulted
          when something is being retired or re-planned, not on every visit, and
          an open panel of "nothing yet" between the description and the work is
          a screenful of nothing. The summary carries the counts, so whether
          there is anything in here is visible from the outside. */}
      <CollapsiblePanel
        title="Dependencies"
        aside={
          <span className="muted">
            {(object.dependencies?.depends_on ?? []).length} out ·{" "}
            {(object.dependencies?.dependents ?? []).length} in · recorded, never enforced
          </span>
        }
      >
        <h3 style={{ fontSize: 12, fontFamily: "var(--display)", margin: "0 0 4px" }}>
          Depends on
        </h3>
        <ul className="deps">
          {(object.dependencies?.depends_on ?? []).map((d) => (
            <li key={`${d.kind}-${d.id}`}>
              <Chip text={d.dep_kind} />
              <a href={href.object(d.kind, d.id)}>{d.name}</a>
              <span className="muted">{d.kind}</span>
              <button
                className="linkbtn"
                style={{ marginLeft: "auto" }}
                onClick={async () => {
                  await run(() =>
                    api.removeDependency(plural(object.kind), object.id, d.kind, d.id),
                  );
                  await load();
                }}
              >
                remove
              </button>
            </li>
          ))}
          {(object.dependencies?.depends_on ?? []).length === 0 && (
            <li className="muted">nothing yet</li>
          )}
        </ul>

        <div className="row" style={{ marginTop: 10 }}>
          <select id="dep-target" defaultValue="">
            <option value="">add a dependency on…</option>
            {candidates.map((o) => (
              <option key={`${o.kind}-${o.id}`} value={`${o.kind}:${o.id}`}>
                {o.name} ({o.kind})
              </option>
            ))}
          </select>
          <select id="dep-kind" defaultValue={meta.dependency_kinds[0]}>
            {meta.dependency_kinds.map((k) => (
              <option key={k}>{k}</option>
            ))}
          </select>
          <button
            className="btn"
            onClick={() => {
              const target = document.getElementById("dep-target") as HTMLSelectElement;
              const depKind = document.getElementById("dep-kind") as HTMLSelectElement;
              if (!target.value) return;
              const [k, i] = target.value.split(":");
              void addDependency(k as Kind, Number(i), depKind.value);
              target.value = "";
            }}
          >
            Add
          </button>
        </div>

        <h3 style={{ fontSize: 12, fontFamily: "var(--display)", margin: "16px 0 4px" }}>
          Depended on by
        </h3>
        <ul className="deps">
          {(object.dependencies?.dependents ?? []).map((d) => (
            <li key={`in-${d.kind}-${d.id}`}>
              <Chip text={d.dep_kind} />
              <a href={href.object(d.kind, d.id)}>{d.name}</a>
              <span className="muted">{d.kind}</span>
            </li>
          ))}
          {(object.dependencies?.dependents ?? []).length === 0 && (
            <li className="muted">nothing depends on this</li>
          )}
        </ul>
      </CollapsiblePanel>
    </>
  );
}

function NewLink({
  objectKind,
  objectId,
  onDone,
}: {
  objectKind: Kind;
  objectId: number;
  onDone: () => Promise<void>;
}) {
  const { run } = useStore();
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");

  const add = async () => {
    const href = url.trim();
    if (!href) return;
    const label = title.trim();
    setUrl("");
    setTitle("");
    await run(() => api.addObjectLink(plural(objectKind), objectId, href, label));
    await onDone();
  };

  return (
    <div className="row" style={{ marginTop: 10 }}>
      <input
        type="text"
        placeholder="https://…"
        style={{ flex: 2 }}
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && void add()}
      />
      <input
        type="text"
        placeholder="label (optional)"
        style={{ flex: 1 }}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && void add()}
      />
      <button className="btn" disabled={!url.trim()} onClick={() => void add()}>
        Add
      </button>
    </div>
  );
}

function NewTask({
  objectKind,
  objectId,
  onDone,
}: {
  objectKind: Kind;
  objectId: number;
  onDone: () => Promise<void>;
}) {
  const { run } = useStore();
  const [title, setTitle] = useState("");
  return (
    <div className="row" style={{ marginTop: 8 }}>
      <input
        type="text"
        className="grow"
        style={{ flex: 1 }}
        placeholder="new task on this object…"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <button
        className="btn"
        disabled={!title.trim()}
        onClick={async () => {
          const text = title.trim();
          setTitle("");
          await run(() =>
            api.createTask({
              title: text,
              status: "in progress",
              attached_to: [{ kind: objectKind, id: objectId }],
            }),
          );
          await onDone();
        }}
      >
        Add
      </button>
    </div>
  );
}
